import apiClient from './client';

// ─── Types (matching backend entity shapes) ──────────────────────────────────

/**
 * Per-day work hour schedule matching the backend DaySchedule JSONB column.
 * Keys are three-letter day abbreviations, values are hours (0 = day off).
 */
export interface DaySchedule {
  mon: number;
  tue: number;
  wed: number;
  thu: number;
  fri: number;
  sat: number;
  sun: number;
}

export const DEFAULT_DAY_SCHEDULE: DaySchedule = {
  mon: 8, tue: 8, wed: 8, thu: 8, fri: 8, sat: 0, sun: 0,
};

export const DAY_KEYS: (keyof DaySchedule)[] = [
  'mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun',
];

/** Capacity scheme as returned by the backend. */
export interface CapacityScheme {
  id: string;
  account_id: string;
  name: string;
  is_default: boolean;
  schedule: DaySchedule;
  created_at: string;
  updated_at: string;
}

/** Assignment of a scheme to a user or team. */
export interface CapacitySchemeAssignment {
  id: string;
  account_id: string;
  scheme_id: string;
  monday_user_id: string | null;
  monday_team_id: string | null;
  created_at: string;
}

// ─── Payloads ────────────────────────────────────────────────────────────────

export interface CreateCapacitySchemePayload {
  name: string;
  isDefault?: boolean;
  schedule?: DaySchedule;
}

export type UpdateCapacitySchemePayload = Partial<CreateCapacitySchemePayload>;

export interface AssignCapacityPayload {
  mondayUserId: string;
}

/** Response from the my-scheme endpoint. */
export interface MyCapacitySchemeResponse {
  scheme: {
    id: string;
    name: string;
    isDefault: boolean;
    schedule: DaySchedule;
  } | null;
  source: 'assigned' | 'default' | null;
}

// ─── Scheme CRUD ─────────────────────────────────────────────────────────────

/** Get the capacity scheme that applies to the current user. */
export async function getMyCapacityScheme(): Promise<MyCapacitySchemeResponse> {
  const { data } = await apiClient.get<MyCapacitySchemeResponse>(
    '/capacity-schemes/my-scheme',
  );
  return data;
}

export async function getCapacitySchemes(): Promise<CapacityScheme[]> {
  const { data } = await apiClient.get<CapacityScheme[]>('/capacity-schemes');
  return data;
}

export async function createCapacityScheme(
  payload: CreateCapacitySchemePayload,
): Promise<CapacityScheme> {
  const { data } = await apiClient.post<CapacityScheme>(
    '/capacity-schemes',
    payload,
  );
  return data;
}

export async function updateCapacityScheme(
  id: string,
  payload: UpdateCapacitySchemePayload,
): Promise<CapacityScheme> {
  const { data } = await apiClient.patch<CapacityScheme>(
    `/capacity-schemes/${id}`,
    payload,
  );
  return data;
}

export async function deleteCapacityScheme(id: string): Promise<void> {
  await apiClient.delete(`/capacity-schemes/${id}`);
}

export async function duplicateCapacityScheme(
  id: string,
): Promise<CapacityScheme> {
  const { data } = await apiClient.post<CapacityScheme>(
    `/capacity-schemes/${id}/duplicate`,
  );
  return data;
}

// ─── Assignments ─────────────────────────────────────────────────────────────

export async function getSchemeAssignments(
  schemeId: string,
): Promise<CapacitySchemeAssignment[]> {
  const { data } = await apiClient.get<CapacitySchemeAssignment[]>(
    `/capacity-schemes/${schemeId}/assignments`,
  );
  return data;
}

export async function createSchemeAssignment(
  schemeId: string,
  payload: AssignCapacityPayload,
): Promise<CapacitySchemeAssignment> {
  const { data } = await apiClient.post<CapacitySchemeAssignment>(
    `/capacity-schemes/${schemeId}/assignments`,
    payload,
  );
  return data;
}

export async function removeSchemeAssignment(
  schemeId: string,
  assignmentId: string,
): Promise<void> {
  await apiClient.delete(
    `/capacity-schemes/${schemeId}/assignments/${assignmentId}`,
  );
}
